// useRpcQuery / useRpcMutation — the single way to read/write Supabase RPCs.
//
// Wraps getBrowserClient().rpc() in TanStack Query so every read gets, for free:
//   • a shared cache keyed by [fn, args]  (dedupes concurrent callers)
//   • stale-while-revalidate: cached data renders instantly, refetch in the bg
//   • refetch on return-to-screen + window focus (see queryClient defaults)
//   • optional interval polling for live surfaces (refetchInterval)
//   • cold-load vs background-refresh distinction (isLoading vs isFetching)
//
// HONEST-EMPTY contract: a null/empty result is a real published state, not an
// error and not "still loading". isLoading is true ONLY on the cold load.

import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getBrowserClient } from "../supabase/browser.ts";

type RpcArgs = Record<string, unknown> | undefined;

interface RpcClient {
  rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
}

async function callRpc<T>(fn: string, args?: RpcArgs): Promise<T> {
  const client = getBrowserClient() as unknown as RpcClient;
  const { data, error } = await client.rpc(fn, args);
  if (error) throw new Error(error.message);
  return (data ?? null) as T;
}

export interface RpcQueryOptions<T, S = T> {
  /** Named args passed to the RPC (e.g. { p_date: '2026-05-29' }). */
  args?: RpcArgs;
  /** Override how long the result stays "fresh" (ms). Long for expensive/LLM reads. */
  staleTime?: number;
  /** Background poll interval (ms). Reserve for live surfaces (slate/odds). */
  refetchInterval?: number;
  /** Skip the query until true (e.g. waiting on an id). */
  enabled?: boolean;
  /** Transform/shape the raw RPC payload. */
  select?: (raw: T) => S;
}

/** Read a Supabase RPC as a cached, auto-refreshing query. */
export function useRpcQuery<T, S = T>(fn: string, opts: RpcQueryOptions<T, S> = {}): UseQueryResult<S> {
  const { args, staleTime, refetchInterval, enabled, select } = opts;
  return useQuery<T, Error, S>({
    queryKey: ["rpc", fn, args ?? null],
    queryFn: () => callRpc<T>(fn, args),
    staleTime,
    refetchInterval,
    enabled,
    select,
  });
}

/** Write/action RPC. Returns the mutation; call .mutate(args) / .mutateAsync(args). */
export function useRpcMutation<TResult = unknown, TArgs extends RpcArgs = RpcArgs>(fn: string) {
  return useMutation<TResult, Error, TArgs>({
    mutationFn: (args: TArgs) => callRpc<TResult>(fn, args),
  });
}
